import React, { useEffect, useMemo, useState } from "react";
import { getHostContext } from "./hostContext";
import { LoginView } from "./views/LoginView";
import { SignupView } from "./views/SignupView";
import { ForgotPasswordView } from "./views/ForgotPasswordView";
import { ResetPasswordView } from "./views/ResetPasswordView";
import { VerifyRecoveryEmailView } from "./views/VerifyRecoveryEmailView";
import { AccountRecoveryView } from "./views/AccountRecoveryView";
import { RecoveryResetView } from "./views/RecoveryResetView";
import { MailView } from "./views/MailView";
import { SettingsView } from "./views/SettingsView";
import { ReaderPage } from "./views/ReaderPage";
import { apiDelete, apiGet } from "./api";
import { getInitialUiSettings, applyReaderMaxHeight } from "./uiSettings";
import { useAppearancePrefs } from "./hooks/useAppearancePrefs";
import { getInitialLocale, type Locale, t } from "./i18n";

type DataMode = "mock" | "api";

function getQueryMessageId(): string | null {
  const u = new URL(window.location.href);
  const v = u.searchParams.get("m");
  return v && v.length > 0 ? v : null;
}

function setQueryMessageId(id: string | null) {
  const u = new URL(window.location.href);
  if (!id) u.searchParams.delete("m");
  else u.searchParams.set("m", id);
  window.history.pushState({}, "", u.toString());
}

export function App() {
  const [locale, setLocale] = useState<Locale>("en");

  const [authedEmail, setAuthedEmail] = useState<string | null>(null);
  const [userFullName, setUserFullName] = useState<string | null>(null);
  const [dataMode, setDataMode] = useState<DataMode>("mock");

  const [readerPageId, setReaderPageId] = useState<string | null>(null);
  const [path, setPath] = useState<string>(() => window.location.pathname);

  const { theme, openMode, timezone, dateFormat, setTheme, setOpenMode, setTimezone, setDateFormat } =
    useAppearancePrefs(dataMode);

  useEffect(() => {
    document.title = getHostContext().documentTitle;

    const l0 = getInitialLocale();
    setLocale(l0);

    const ui0 = getInitialUiSettings();
    applyReaderMaxHeight(ui0.readerMaxViewportRatio);

    setReaderPageId(getQueryMessageId());
    setPath(window.location.pathname);


    function onPop() {
      setReaderPageId(getQueryMessageId());
      setPath(window.location.pathname);

    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const modeLabel = useMemo(() => {
    if (!authedEmail) return t(locale, "app.mode.login");
    return dataMode === "api" ? t(locale, "app.mode.api") : t(locale, "app.mode.mock");
  }, [authedEmail, dataMode, locale]);

  async function logout() {
    await apiDelete("/v1/session");
    setAuthedEmail(null);
    setUserFullName(null);
    setDataMode("mock");
    setQueryMessageId(null);
    setReaderPageId(null);
  }

  async function fetchUserFullName() {
    try {
      const res = await apiGet<{ full_name?: string | null }>("/v1/profile/personal-info");
      if (res.ok && res.data.full_name) {
        setUserFullName(res.data.full_name);
      }
    } catch {
      // Silently ignore errors; full_name is optional
    }
  }

  function openMessage(id: string) {
    if (openMode === "page") {
      setQueryMessageId(id);
      setReaderPageId(id);
      return;
    }
    // in-list or window -> open new tab/window
    const url = new URL(window.location.href);
    url.searchParams.set("m", id);
    window.open(url.toString(), "_blank", "noopener,noreferrer");
  }

  function backFromReaderPage() {
    setQueryMessageId(null);
    setReaderPageId(null);
  }

  return (
    <>
      {!authedEmail ? (
        path.startsWith("/signup") ? (
          <SignupView locale={locale} onAuthed={(email) => {
            setAuthedEmail(email);
            setDataMode("api");
            // Fetch user's full name if available
            (async () => {
              try {
                const res = await apiGet<{ full_name?: string | null }>("/v1/profile/personal-info");
                if (res.ok && res.data.full_name) {
                  setUserFullName(res.data.full_name);
                }
              } catch {
                // Silently ignore; full_name is optional
              }
            })();
          }} />
        ) : path.startsWith("/recover/reset") ? (
          <RecoveryResetView locale={locale} />
        ) : path.startsWith("/recover") ? (
          <AccountRecoveryView locale={locale} />
        ) : path.startsWith("/forgot-password") ? (
          <ForgotPasswordView locale={locale} />
        ) : path.startsWith("/reset-password") ? (
          <ResetPasswordView locale={locale} />
        ) : path.startsWith("/verify-recovery-email") ? (
          <VerifyRecoveryEmailView locale={locale} />
        ) : (
        <>
          {/* Keep the login view as-is (it includes its own minimal styling) */}
          <LoginView locale={locale} onAuthed={(email) => {
            setAuthedEmail(email);
            setDataMode("api");
            // Fetch user's full name if available
            (async () => {
              try {
                const res = await apiGet<{ full_name?: string | null }>("/v1/profile/personal-info");
                if (res.ok && res.data.full_name) {
                  setUserFullName(res.data.full_name);
                }
              } catch {
                // Silently ignore; full_name is optional
              }
            })();
          }} />
          <div style={{ padding: 12, textAlign: "center" }} className="small">
            {modeLabel}
          </div>
        </>
        )
      ) : path.startsWith("/settings") ? (
        <SettingsView
          locale={locale}
          theme={theme}
          openMode={openMode}
          timezone={timezone}
          dateFormat={dateFormat}
          dataMode={dataMode}
          onSetTheme={setTheme}
          onSetOpenMode={setOpenMode}
          onSetTimezone={setTimezone}
          onSetDateFormat={setDateFormat}
          onSetDataMode={setDataMode}
          onBack={() => { setPath("/"); window.history.pushState({}, "", "/"); }}
        />
      ) : readerPageId && openMode === "page" ? (
        <ReaderPage
          locale={locale}
          dataMode={dataMode}
          messageId={readerPageId}
          onBack={backFromReaderPage}
        />
      ) : (
        <MailView
          locale={locale}
          dataMode={dataMode}
          openMode={openMode}
          onOpenMessage={openMessage}
          authedEmail={authedEmail}
          userFullName={userFullName}
          onLogout={logout}
          onSetOpenMode={setOpenMode}
          onSetDataMode={setDataMode}
        />
      )}
    </>
  );
}
